import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core'
import { MatSort } from '@angular/material/sort'
import { select, Store } from '@ngrx/store'
import { DocumentTemplateFilters } from './template-list-filter'
import { filter, map, switchMap, take, tap, concatMap, toArray, startWith } from 'rxjs/operators'
import { TemplateMessageDialogService } from '../dialogs/message/template-message-dialog.service'
import { combineLatest, of } from 'rxjs'
import { FormBuilder } from '@angular/forms'
import { CreateTemplateDrawerService } from '../template-drawer/create-template/create-template-drawer.service'
import { EditTemplateDrawerService } from '../template-drawer/edit-template/edit-template-drawer.service'
import { TemplateDrawerTab } from '../template-drawer/edit-template/edit-template-drawer.component'
import { PreviewTemplateDrawerService } from '../template-drawer/preview-template/preview-template-drawer.service'
import { RenameTemplateDialogService } from '../dialogs/rename/rename-template-dialog.service'
import { MatPaginator } from '@angular/material/paginator'
import {
  ContractTemplateView,
  DeleteContractTemplate,
  DeleteMultipleContractTemplate,
  selectAllContractTemplateTags,
  selectContractTemplatesLoaded,
  selectContractTemplateViews,
  SignatureFileService,
  templateTypeOptions,
  TemplateTypes,
  UpdateContractTemplate,
} from '@tokeet-frontend/signature'
import {
  ConfirmDialogService,
  isEmptyTable,
  localeCompareSort,
  SelectableRow,
  Toaster,
  untilDestroy,
} from '@tokeet-frontend/tv3-platform'
import { SignPrepareService } from '@tv3/services/sign-prepare.service'
import { SendTemplateDrawerService } from '@tv3/containers/documents/templates/send-drawer/send-template/send-template-drawer.service'
import { DeleteAutomation, selectAutomationGroupsByTemplateId } from '@tokeet-frontend/automations'

@Component({
  selector: 'app-document-templates',
  templateUrl: './document-templates.component.html',
  styleUrls: ['./document-templates.component.scss'],
  providers: [DocumentTemplateFilters],
})
export class DocumentTemplatesComponent extends SelectableRow<ContractTemplateView> implements OnInit {
  @ViewChild(MatSort) set sort(sort: MatSort) {
    this.dataSource.sort = sort
  }

  @ViewChild('paginator') set paginator(paginator: MatPaginator) {
    this.dataSource.paginator = paginator
  }
  get paginator() {
    return this.dataSource.paginator
  }

  isLoading$ = this.store.pipe(
    select(selectContractTemplatesLoaded),
    map((loaded) => !loaded)
  )

  displayedColumns: string[] = ['created', 'name', 'typeView', 'roles', 'edit']

  isEmptyTable$ = isEmptyTable(this.dataSource)
  filters = this.templateFilters.group
  types = templateTypeOptions
  tags$ = this.store.pipe(select(selectAllContractTemplateTags))
  isEmpty$ = this.templateFilters.group.origin.pipe(map((data) => !data?.length))

  constructor(
    private store: Store<any>,
    private toaster: Toaster,
    private templateFilters: DocumentTemplateFilters,
    private confirmDialog: ConfirmDialogService,
    private templateMessageDialog: TemplateMessageDialogService,
    private signPrepareService: SignPrepareService,
    private fileService: SignatureFileService,
    private createTemplateDrawer: CreateTemplateDrawerService,
    private editTemplateDrawer: EditTemplateDrawerService,
    private sendTemplateDrawer: SendTemplateDrawerService,
    private previewTemplateDrawer: PreviewTemplateDrawerService,
    private renameDialog: RenameTemplateDialogService
  ) {
    super()
  }

  ngOnInit(): void {
    this.dataSource.sort = this.sort
    this.dataSource.paginator = this.paginator
    this.dataSource.sortData = localeCompareSort

    this.templateFilters.group.changes.pipe(untilDestroy(this)).subscribe((items) => {
      this.dataSource.data = items
    })

    this.templateFilters.group.currentFilters.pipe(untilDestroy(this)).subscribe(() => {
      this.paginator?.firstPage()
    })
  }

  isReadyToUse(item: ContractTemplateView) {
    return this.signPrepareService.isTemplateReady(item.type, item.roles, item.fields)
  }

  onSearch(term: string) {
    this.paginator?.firstPage()
    this.dataSource.filter = (term + '').trim().toLowerCase()
  }

  onDelete(item: ContractTemplateView) {
    this.store
      .pipe(
        select(selectAutomationGroupsByTemplateId(item.template_id)),
        take(1),
        switchMap((triggers) => {
          if (triggers?.length > 0) {
            return this.confirmDialog
              .confirm({
                title: $localize`Delete associated automations?`,
                body: $localize`This template has one or more automations associated with it. Deleting this template will also delete the automation. This will also affect any automations created for this template in Automata. Would you like to proceed?`,
              })
              .pipe(
                tap(() => {
                  this.store.dispatch(DeleteContractTemplate({ id: item.id }))
                  this.store.dispatch(DeleteAutomation({ ids: triggers.map((t) => t.items.map((i) => i.pkey)).flat() }))
                })
              )
          } else {
            return this.confirmDialog
              .confirm({
                title: $localize`Confirm`,
                body: $localize`Are you sure that you want to delete this template?`,
              })
              .pipe(
                tap(() => {
                  this.store.dispatch(DeleteContractTemplate({ id: item.id }))
                })
              )
          }
        })
      )
      .subscribe()
  }

  onDownload(template: ContractTemplateView) {
    this.fileService.downloadToLocal(template.pdf_file_url, `${template.name}.pdf`).subscribe()
  }

  onMessage(item: ContractTemplateView) {
    this.templateMessageDialog.open(item)
  }

  onAdd() {
    this.createTemplateDrawer
      .open()
      .afterClosed()
      .pipe(
        filter((id) => !!id),
        switchMap((id) => this.editTemplateDrawer.open(id).afterClosed())
      )
      .subscribe()
  }

  onEdit(item: ContractTemplateView) {
    this.editTemplateDrawer.open(item.id, TemplateDrawerTab.Details)
  }

  onSend(item: ContractTemplateView) {
    this.sendTemplateDrawer.open(item.template_id)
  }

  onPreview(item: ContractTemplateView) {
    this.previewTemplateDrawer.open({
      type: item.type,
      fields: item.fields,
      pdfFileUrl: item.pdf_file_url,
      defaultFont: item.font,
      defaultFontSize: item.font_size,
    })
  }

  onPrepare(item: ContractTemplateView) {
    this.editTemplateDrawer.open(item.id, TemplateDrawerTab.Fields)
  }

  onRename(item: ContractTemplateView) {
    this.renameDialog.open(item)
  }

  onDownloadSelected() {
    const selectedItems = this.getSelected()
    if (!selectedItems.length) {
      this.toaster.warning($localize`Please select at least one template.`)
      return
    }
    of(...selectedItems)
      .pipe(
        concatMap((template) => this.fileService.downloadToLocal(template.pdf_file_url, `${template.name}.pdf`)),
        toArray()
      )
      .subscribe(() => {
        this.clearSelected()
      })
  }

  onDeleteSelected() {
    const selectedItems = this.getSelected()
    const selectedIds = selectedItems.map((i) => i.id)
    if (!selectedIds.length) {
      this.toaster.warning($localize`Please select at least one template.`)
      return
    }
    this.confirmDialog
      .confirm({
        title: $localize`Confirm`,
        body: $localize`Are you sure that you want to delete selected template(s)?`,
      })
      .subscribe(() => {
        this.store.dispatch(DeleteMultipleContractTemplate({ ids: selectedIds }))
        this.clearSelected()
      })
  }
}
